import React, { useEffect, useState } from 'react';
import axios from 'axios';
import './PublicPhotoViewer.css';

const API_BASE = process.env.REACT_APP_PUBLIC_API_URL || 'http://localhost:3002/api/public';

function PublicPhotoViewer({ photo, photos = [], tenantName, tenant, albumName, onClose, onNavigate }) {
    const [photoDetails, setPhotoDetails] = useState(null);
    const [likes, setLikes] = useState({ likeCount: 0, hasLiked: false });
    const [showExif, setShowExif] = useState(false);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [imageLoading, setImageLoading] = useState(true);

    const currentIndex = photos.findIndex(p => p.id === photo.id);
    const hasPrevious = currentIndex > 0;
    const hasNext = currentIndex < photos.length - 1;

    const handlePrevious = () => {
        if (hasPrevious && onNavigate) {
            onNavigate(photos[currentIndex - 1]);
        }
    };

    const handleNext = () => {
        if (hasNext && onNavigate) {
            onNavigate(photos[currentIndex + 1]);
        }
    };

    useEffect(() => {
        fetchPhotoDetails();
        fetchLikes();
        setShowExif(false);
        setImageLoading(true);
    }, [photo.id]);

    // Format EXIF field names
    const formatFieldName = (key) => {
        const nameMap = {
            'ISO': 'ISO',
            'FNumber': 'Aperture',
            'ExposureTime': 'Shutter Speed',
            'FocalLength': 'Focal Length',
            'FocalLengthIn35mmFormat': 'Focal Length (35mm)',
            'LensModel': 'Lens Model',
            'LensMake': 'Lens Make',
            'DateTimeOriginal': 'Date Taken',
            'CreateDate': 'Create Date',
            'ImageWidth': 'Dimensions',
            'ExifImageWidth': 'Dimensions',
            'Make': 'Make',
            'Model': 'Model'
        };
        if (nameMap[key]) return nameMap[key];
        return key.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase()).trim();
    };

    // Format EXIF values
    const formatMetadataValue = (key, value, metadata) => {
        if (value === null || value === undefined) return 'N/A';
        if (key === 'FNumber' && typeof value === 'number') return `f/${value}`;
        if (key === 'ExposureTime' && typeof value === 'number') {
            return value < 1 ? `1/${Math.round(1 / value)}s` : `${value}s`;
        }
        if ((key === 'FocalLength' || key === 'FocalLengthIn35mmFormat') && typeof value === 'number') {
            return `${value}mm`;
        }
        if ((key === 'ImageWidth' || key === 'ExifImageWidth') && typeof value === 'number') {
            const heightKey = key.replace('Width', 'Height');
            const height = metadata?.[heightKey];
            return height ? `${value} x ${height} pixels` : `${value} pixels`;
        }
        if (key === 'ImageHeight' || key === 'ExifImageHeight') return null;
        // Format EXIF date strings (e.g., "2024:01:15 14:30:00" or ISO "2024-01-15T14:30:00.000Z")
        if ((key === 'DateTimeOriginal' || key === 'CreateDate') && typeof value === 'string') {
            // Handle EXIF format (colons in date) or ISO format
            const parsed = value.replace(/^(\d{4}):(\d{2}):(\d{2})/, '$1-$2-$3');
            const date = new Date(parsed);
            if (!isNaN(date.getTime())) {
                return date.toLocaleDateString(undefined, {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                });
            }
            return value;
        }
        if (typeof value === 'boolean') return value ? 'Yes' : 'No';
        if (Array.isArray(value)) return value.join(', ');
        if (typeof value === 'object') return JSON.stringify(value);
        if (value instanceof Date) return value.toLocaleString();
        return String(value);
    };

    const categorizeMetadata = (metadata) => {
        const allowedFields = {
            'Camera': ['Make', 'Model'],
            'Lens': ['LensModel', 'LensMake'],
            'Exposure': ['ISO', 'FNumber', 'ExposureTime', 'FocalLength', 'FocalLengthIn35mmFormat'],
            'Image': ['ImageWidth', 'ExifImageWidth'],
            'Date & Time': ['DateTimeOriginal']
        };
        const categories = { 'Camera': [], 'Lens': [], 'Exposure': [], 'Image': [], 'Date & Time': [] };
        Object.entries(metadata).forEach(([key, value]) => {
            for (const [category, fields] of Object.entries(allowedFields)) {
                if (fields.includes(key)) {
                    const formattedValue = formatMetadataValue(key, value, metadata);
                    if (formattedValue === null) return;
                    categories[category].push({ key, label: formatFieldName(key), value: formattedValue });
                    break;
                }
            }
        });
        return Object.fromEntries(Object.entries(categories).filter(([, items]) => items.length > 0));
    };

    const fetchPhotoDetails = async () => {
        try {
            const response = await axios.get(`${API_BASE}/${tenantName}/photos/${photo.id}`);
            setPhotoDetails(response.data);
        } catch (error) {
            console.error('Error fetching photo details:', error);
        }
    };

    const fetchLikes = async () => {
        try {
            const response = await axios.get(`${API_BASE}/${tenantName}/photos/${photo.id}/likes`);
            setLikes(response.data);
        } catch (error) {
            console.error('Error fetching likes:', error);
        }
    };

    const handleLikeToggle = async () => {
        try {
            if (likes.hasLiked) {
                await axios.delete(`${API_BASE}/${tenantName}/photos/${photo.id}/like`);
            } else {
                await axios.post(`${API_BASE}/${tenantName}/photos/${photo.id}/like`);
            }
            // Refresh likes
            fetchLikes();
        } catch (error) {
            console.error('Error toggling like:', error);
        }
    };

    const toggleFullscreen = () => {
        setIsFullscreen(!isFullscreen);
    };

    const metadata = photoDetails?.metadata || {};
    const categorizedMetadata = categorizeMetadata(metadata);
    const hasExif = Object.keys(categorizedMetadata).length > 0;

    // Convert URL album name format back to display format
    const displayAlbumName = albumName ? albumName.replace(/-/g, ' ') : null;

    return (
        <div className="public-photo-viewer-overlay" onClick={onClose}>
            <div className="public-photo-viewer-container" onClick={(e) => e.stopPropagation()}>
                <header className="viewer-banner">
                    <h1>{tenant?.public_title || `${tenant?.name} Gallery`}</h1>
                    {displayAlbumName && (
                        <p className="viewer-album-name">{displayAlbumName}</p>
                    )}
                    <div className="viewer-banner-buttons">
                        <button
                            className="fullscreen-btn"
                            onClick={toggleFullscreen}
                            title="Fullscreen view"
                            aria-label="Fullscreen view"
                        >
                            ⛶
                        </button>
                        {hasExif && (
                            <button
                                className="info-btn"
                                onClick={() => setShowExif(true)}
                                title="Photo info"
                                aria-label="Show photo info"
                            >
                                i
                            </button>
                        )}
                        <button className="close-btn" onClick={onClose}>&times;</button>
                    </div>
                </header>
                {showExif && (
                    <div className="exif-popup-overlay" onClick={() => setShowExif(false)}>
                        <div className="exif-popup" onClick={(e) => e.stopPropagation()}>
                            <button className="exif-popup-close" onClick={() => setShowExif(false)}>&times;</button>
                            <h3 className="exif-popup-title">Photo Info</h3>
                            <div className="exif-popup-content">
                                {Object.entries(categorizedMetadata).map(([category, items]) => (
                                    <div key={category} className="exif-category">
                                        <h4>{category}</h4>
                                        <dl>
                                            {items.map(item => (
                                                <div key={item.key} className="exif-row">
                                                    <dt>{item.label}</dt>
                                                    <dd>{item.value}</dd>
                                                </div>
                                            ))}
                                        </dl>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                )}

                {isFullscreen && (
                    <div className="fullscreen-overlay" onClick={toggleFullscreen}>
                        <button className="fullscreen-close-btn" onClick={toggleFullscreen}>&times;</button>
                        {imageLoading && (
                            <div className="image-loading-spinner">
                                <div className="spinner"></div>
                            </div>
                        )}
                        <img
                            src={`${API_BASE}/${tenantName}/photos/${photo.id}/file`}
                            alt={photo.title || ''}
                            style={{ opacity: imageLoading ? 0 : 1 }}
                        />
                    </div>
                )}

                <div className="public-photo-viewer-content">
                    <div className="public-photo-viewer-image">
                        {imageLoading && (
                            <div className="image-loading-spinner">
                                <div className="spinner"></div>
                            </div>
                        )}
                        <img
                            src={`${API_BASE}/${tenantName}/photos/${photo.id}/file`}
                            alt={photo.title || ''}
                            onLoad={() => setImageLoading(false)}
                            style={{ opacity: imageLoading ? 0 : 1 }}
                        />
                    </div>

                    <div className="public-photo-viewer-info">
                        <div className="viewer-info-header">
                            {photos.length > 1 && (
                                <div className="viewer-navigation-subtle">
                                    <button
                                        className={`nav-link ${!hasPrevious ? 'disabled' : ''}`}
                                        onClick={handlePrevious}
                                        disabled={!hasPrevious}
                                        title="Previous"
                                    >
                                        &lsaquo;
                                    </button>
                                    <span className="nav-counter-subtle">
                                        {currentIndex + 1}/{photos.length}
                                    </span>
                                    <button
                                        className={`nav-link ${!hasNext ? 'disabled' : ''}`}
                                        onClick={handleNext}
                                        disabled={!hasNext}
                                        title="Next"
                                    >
                                        &rsaquo;
                                    </button>
                                </div>
                            )}
                            <div className="viewer-info-title">
                                {photo.title && <h2>{photo.title}</h2>}
                                {photoDetails?.album_name && (
                                    <p className="photo-album-name">Album: {photoDetails.album_name}</p>
                                )}
                                <p className="photo-upload-date">
                                    Uploaded: {new Date(photo.uploaded_at).toLocaleDateString()}
                                </p>
                            </div>
                            <div className="viewer-like-section">
                                <button
                                    className={`like-btn ${likes.hasLiked ? 'liked' : ''}`}
                                    onClick={handleLikeToggle}
                                >
                                    {likes.hasLiked ? '❤' : '♡'}
                                </button>
                                <span className="viewer-like-count">{likes.likeCount} likes</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default PublicPhotoViewer;
